const STORAGE_KEY = 'coredna_person';
const GEOLOCATION_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

class Personalisation {
	static #instance = null;
	#trackers = new Map();
	#serverSnapshotStr = '';
	userId = null;

	constructor() {
		if (Personalisation.#instance) {
			return Personalisation.#instance;
		}

		this._initStorage();
		this._registerCoreTrackers();
		Personalisation.#instance = this;
	}

	static getInstance() {
		if (!this.#instance) {
			this.#instance = new Personalisation();
		}
		return this.#instance;
	}

	static async initialize() {
		const instance = new Personalisation();

		// Process global data
		if (window.__PERSONALISATION_DATA__?.pageId) {
			instance.track('pageVisits', window.__PERSONALISATION_DATA__.pageId);
		}

		// Update geolocation
		await instance.updateGeolocation().catch(console.error);

		// Start observing for asynchronously injected user data
		instance._observeUserData();

		// Dispatch ready event
		document.dispatchEvent(new CustomEvent('coredna:personalisationReady', {
			detail: { instance }
		}));

		return instance;
	}

	_initStorage() {
		const storageData = this._getStorage();
		if (!storageData.customisation) {
			this._saveStorage({
				customisation: {
					visits: { pages: [] },
					location: {
						country: null,
						region: null,
						updatedAt: null
					}
				}
			});
		}
	}

	_registerCoreTrackers() {
		this.registerTracker('pageVisits', {
			storagePath: 'customisation.visits.pages',
			track: (id, currentItems = []) => [
				id,
				...currentItems.filter(item => item !== id)
			].slice(0, 3),
			validate: id => id != null
		});
	}

	async _fetchGeolocation() {
		try {
			const response = await fetch('https://www.coredna.com/geolocation');
			if (!response.ok) throw new Error('Geolocation request failed');
			return await response.json();
		} catch (error) {
			console.warn('Geolocation fetch failed:', error);
			return null;
		}
	}

	async updateGeolocation(forceRefresh = false) {
		const storage = this._getStorage();
		const locationData = storage.customisation?.location || {};

		// Skip if we have recent data and not forcing refresh
		if (!forceRefresh &&
				locationData.updatedAt &&
				Date.now() - new Date(locationData.updatedAt).getTime() < GEOLOCATION_TTL) {
			return false;
		}

		const geoData = await this._fetchGeolocation();
		if (!geoData) return false;

		const newLocation = {
			country: geoData.country || null,
			region: geoData.region || null,
			updatedAt: new Date().toISOString()
		};

		this._saveStorage({
			...storage,
			customisation: {
				...storage.customisation,
				location: newLocation
			}
		});

		return true;
	}

	getGeolocation() {
		const storage = this._getStorage();
		return storage.customisation?.location || {
			country: null,
			region: null,
			updatedAt: null
		};
	}

	registerTracker(name, { storagePath, track, validate } = {}) {
		this.#trackers.set(name, {
			storagePath,
			track: track || ((id, items) => [...items, id]),
			validate: validate || (id => id != null)
		});
	}

	track(name, id) {
		const tracker = this.#trackers.get(name);
		if (!tracker) {
			console.warn(`Tracker "${name}" not registered`);
			return false;
		}

		if (tracker.validate && !tracker.validate(id)) {
			console.warn(`Invalid ID for tracker "${name}":`, id);
			return false;
		}

		const storage = this._getStorage();
		const pathParts = tracker.storagePath.split('.');
		const lastKey = pathParts.pop();

		let current = storage;
		for (const key of pathParts) {
			current[key] = current[key] || {};
			current = current[key];
		}

		current[lastKey] = tracker.track(id, current[lastKey] || []);
		this._saveStorage(storage);

		return true;
	}

	getTrackedItems(name) {
		const tracker = this.#trackers.get(name);
		if (!tracker) return null;

		const storage = this._getStorage();
		return tracker.storagePath.split('.').reduce(
				(obj, key) => obj?.[key], storage
		) || [];
	}

	/**
	 * Generates tracking query string with page visits and location
	 * @returns {string} Formatted as "tp=id1,id2,id3&loc=country_region"
	 */
	getTrackingQueryString() {
		const storage = this._getStorage();

		// Get page visits (top 3)
		const pageVisits = storage.customisation?.visits?.pages || [];
		const tpValue = pageVisits.slice(0, 3).join(',');

		// Get location data
		const location = storage.customisation?.location || {};
		let locValue = '';

		if (location.country && location.region) {
			locValue = `${location.country.toLowerCase()}_${location.region.toLowerCase()}`;
		} else if (location.country) {
			locValue = location.country.toLowerCase();
		}

		// Build query string
		const params = [];
		if (tpValue) params.push(`tp=${encodeURIComponent(tpValue)}`);
		if (locValue) params.push(`loc=${encodeURIComponent(locValue)}`);

		return params.join('&');
	}

	_observeUserData() {
		const parseAndCall = (el) => {
			if (!el) return;
			const uidAttr = el.getAttribute('data-user-id');
			const pagesAttr = el.getAttribute('data-pages') || '';
			const userId = uidAttr == null ? null : (Number.isNaN(Number(uidAttr)) ? uidAttr : Number(uidAttr));
			const pages = pagesAttr
					.split(',')
					.map(s => s.trim())
					.filter(Boolean);
			this.syncUserData(userId, pages);
		};

		const mo = new MutationObserver((mutations, observer) => {
			const el = document.getElementById('current-user');
			if (el) {
				parseAndCall(el);
				observer.disconnect();
			}
		});

		mo.observe(document.documentElement, { childList: true, subtree: true });
	}

	syncUserData(userId, pages) {
		this.userId = userId ?? null;
		const serverPages = Array.isArray(pages) ? pages.map(p => String(p)) : [];
		const serverStr = serverPages.join(',');

		// Read & prepare storage (add minimal auth schema if missing)
		const storage = this._getStorage();
		if (!storage.auth) storage.auth = { userId: null };
		if (!storage.customisation) storage.customisation = { visits: { pages: [] } };
		if (!storage.customisation.visits) storage.customisation.visits = { pages: [] };

		const storedUserId = storage.auth.userId;

		// Case 1: user switched (or first time seen) → OVERWRITE local with server
		if (storedUserId !== this.userId) {
			storage.auth.userId = this.userId;
			storage.customisation.visits.pages = [...serverPages];
			this._saveStorage(storage);

			// Snapshot reflects server state after overwrite
			this.#serverSnapshotStr = serverStr;
			return; // No webhook here
		}

		// Case 2: same user → decide if we need to sync TO server
		const localPages = storage.customisation.visits.pages || [];
		const localStr = localPages.join(',');

		// Record what the server reported this time
		this.#serverSnapshotStr = serverStr;

		// Conditions to POST:
		if (this.userId != null && localStr !== serverStr && navigator.onLine) {
			try {
				// CHANGED: convert payload pages to numbers before sending
				const numericPages = localPages.map(p => Number(p)).filter(n => !Number.isNaN(n));

				fetch('/cdna-api/webhook/fvZBN-xt1kW-Mk1EF-rKSru-I3E3S-3jrCC', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					credentials: 'include',
					body: JSON.stringify({ pages: numericPages }),
					keepalive: true
				}).catch(() => {}); // explicitly ignore result

				// After sending, assume server is (or will be) in sync with local
				this.#serverSnapshotStr = localStr;
			} catch (e) {
				// Intentionally soft-fail
				console.warn('[Personalisation] profile sync failed:', e);
			}
		}
	}


	_getStorage() {
		return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
	}

	_saveStorage(data) {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
	}
}

export default Personalisation;